import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Hourglass, CheckCircle, XCircle, Stethoscope, Note, MagnifyingGlass, UploadSimple, FileText, ShareNetwork, CalendarBlank, User, Users, Trash, Clock, MapPin, WarningCircle } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { DateTimeField } from "@/components/datetime";
import { api, formatApiErrorDetail } from "@/lib/api";
import Footer from "@/components/Footer";
import MedicalRecordPreviewModal from "@/components/MedicalRecordPreviewModal";
import { InlineLoader } from "@/components/LoadingSkeleton";

const STATUS_META = {
  waiting: { label: "Waiting", color: "#6B7F74", icon: Hourglass },
  in_progress: { label: "It's your turn!", color: "#C57053", icon: Stethoscope },
  completed: { label: "Completed", color: "#224032", icon: CheckCircle },
  cancelled: { label: "Cancelled", color: "#8A2A2A", icon: XCircle },
  no_show: { label: "No Show", color: "#8A2A2A", icon: XCircle },
  on_hold: { label: "On Hold", color: "#C57053", icon: Hourglass },
  reschedule_pending: { label: "Needs rescheduling", color: "#B45309", icon: WarningCircle },
};

export default function PatientDashboard() {
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [records, setRecords] = useState([]);
  const [recordsOpen, setRecordsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: "", record_type: "report", file: null });
  const [previewRecord, setPreviewRecord] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({ start_date: "", end_date: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState({ open: false, id: null });
  const [cancelReason, setCancelReason] = useState("");

  const load = async () => {
    const historyParams = new URLSearchParams({ limit: "50" });
    if (historyFilters.start_date) historyParams.set("start_date", historyFilters.start_date);
    if (historyFilters.end_date) historyParams.set("end_date", historyFilters.end_date);
    const [apptResult, histResult, medResult] = await Promise.allSettled([
      api.get("/appointments/me"),
      api.get(`/appointments/history?${historyParams.toString()}`),
      api.get("/medical-records"),
    ]);
    if (apptResult.status === "fulfilled") {
      setItems(apptResult.value.data.items || []);
    }
    if (histResult.status === "fulfilled") {
      setHistory(histResult.value.data.items || []);
    }
    if (medResult.status === "fulfilled") {
      setRecords(medResult.value.data.records || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    let timeoutId = null;
    const safeLoad = async () => {
      await load();
      if (mounted) {
        timeoutId = setTimeout(safeLoad, 6000);
      }
    };
    safeLoad();
    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyFilters.start_date, historyFilters.end_date]);

  const cancel = async (appt_id) => {
    setCancelReason("");
    setCancelDialog({ open: true, id: appt_id });
  };

  const confirmCancel = async () => {
    const { id } = cancelDialog;
    try {
      await api.patch(`/appointments/${id}`, { reason: cancelReason || "Cancelled by patient" });
      toast.success("Appointment cancelled");
      setCancelDialog({ open: false, id: null });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not cancel");
    }
  };

  const reschedule = async (appt_id, queue_date, slot_start_time, slot_end_time) => {
    try {
      await api.patch(`/appointments/${appt_id}/reschedule`, { queue_date, slot_start_time, slot_end_time });
      toast.success("Appointment rescheduled");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not reschedule");
    }
  };

  const uploadRecord = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error("Choose a file to upload");
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("title", uploadForm.title || uploadForm.file.name);
      data.append("record_type", uploadForm.record_type);
      data.append("file", uploadForm.file);
      await api.post("/medical-records", data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Medical record uploaded");
      setUploadForm({ title: "", record_type: "report", file: null });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not upload record");
    } finally {
      setUploading(false);
    }
  };

  const deleteRecord = async (recordId) => {
    if (!window.confirm("Delete this medical record? This cannot be undone.")) return;
    try {
      await api.delete(`/medical-records/${recordId}`);
      toast.success("Record deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not delete record");
    }
  };

  const allItems = [...items, ...history.map((h) => ({ appointment: h.appointment, doctor: h.doctor }))];

  const unique = [];
  const seen = new Set();
  for (const it of allItems) {
    if (!seen.has(it.appointment.id)) {
      seen.add(it.appointment.id);
      unique.push(it);
    }
  }

  const filtered = unique.filter((it) => {
    const a = it.appointment;
    const d = it.doctor;
    const q = searchQuery.toLowerCase();
    if (q && !(d?.name || a.doctor_name || "").toLowerCase().includes(q) && !(a.reason || "").toLowerCase().includes(q) && !String(a.token_number).includes(q) && !(a.queue_date || "").includes(q)) return false;
    if (statusFilter !== "all" && a.status !== statusFilter) return false;
    return true;
  });

  filtered.sort((a, b) => new Date(b.appointment.queue_date) - new Date(a.appointment.queue_date));

  return (
    <div className="min-h-screen bg-background" data-testid="patient-dashboard">
      <Navbar />

      <main className="workspace-page py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">My bookings</div>
            <h1 className="font-display font-bold text-foreground text-3xl sm:text-4xl lg:text-5xl tracking-tight">All appointments</h1>
          </div>
          <Link to="/doctors">
            <Button className="w-full sm:w-auto rounded-full h-11 px-5 bg-primary text-primary-foreground hover:bg-[#1a3025]">
              <MagnifyingGlass size={16} weight="duotone" /> <span className="ml-1.5">Book a doctor</span>
            </Button>
          </Link>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by doctor, reason, token, or date..."
              className="h-11 pl-10 pr-4 rounded-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-11 rounded-full border border-input bg-background px-4 text-sm font-semibold text-foreground"
          >
            <option value="all">All status</option>
            <option value="waiting">Waiting</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no_show">No show</option>
            <option value="reschedule_pending">Needs rescheduling</option>
          </select>
          <div className="flex gap-2">
            <div>
              <DateTimeField
                mode="date"
                value={historyFilters.start_date}
                onChange={(value) => setHistoryFilters((s) => ({ ...s, start_date: value }))}
                placeholder="From date"
              />
            </div>
            <div>
              <DateTimeField
                mode="date"
                value={historyFilters.end_date}
                onChange={(value) => setHistoryFilters((s) => ({ ...s, end_date: value }))}
                minDate={historyFilters.start_date}
                placeholder="To date"
              />
            </div>
            {(searchQuery || statusFilter !== "all" || historyFilters.start_date || historyFilters.end_date) && (
              <Button
                variant="ghost"
                onClick={() => { setSearchQuery(""); setStatusFilter("all"); setHistoryFilters({ start_date: "", end_date: "" }); }}
                className="rounded-full h-11 px-4"
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Bookings list */}
        {loading ? (
          <InlineLoader message="Loading bookings" subtext="Preparing your appointments" />
        ) : filtered.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {filtered.map((it) => {
              const a = it.appointment;
              const d = it.doctor;
              const meta = STATUS_META[a.status] || { label: a.status, color: "#6B7F74", icon: Hourglass };
              const Icon = meta.icon;
              const isActive = a.status === "waiting" || a.status === "in_progress";
              const isReschedulePending = a.status === "reschedule_pending";
              return (
                <div
                  key={a.id}
                  className={`h-full rounded-3xl border p-5 sm:p-6 transition-all ${isReschedulePending ? "border-amber-200 bg-amber-50/50" : isActive ? "border-primary bg-primary/5" : "border-border bg-card"}`}
                  data-testid={`booking-row-${a.id}`}
                >
                  <div className="flex flex-wrap items-start gap-4">
                    <div className="font-token text-3xl sm:text-4xl text-primary w-16">#{String(a.token_number).padStart(2, "0")}</div>
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-display font-bold text-lg text-foreground leading-tight">{d?.name || a.doctor_name}</div>
                      {d?.specialty && <div className="text-xs uppercase tracking-[0.18em] text-accent">{d.specialty}</div>}
                      <div className="text-sm text-muted-foreground mt-1 line-clamp-1">{a.reason}</div>
                      <div className="text-xs text-muted-foreground mt-1">{a.queue_date}</div>
                      {a.clinic_name_snapshot && (
                        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                          <MapPin size={10} weight="duotone" />
                          <span>{a.clinic_name_snapshot}{a.clinic_address_snapshot ? ` — ${a.clinic_address_snapshot}` : ""}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider" style={{ backgroundColor: meta.color + "1A", color: meta.color }}>
                        <Icon size={12} weight="duotone" /> {meta.label}
                      </span>
                      {a.status === "cancelled" && (a.cancelled_by || a.cancellation_reason) && (
                        <span className="inline-flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-xl px-3 py-1 text-xs">
                          {a.cancelled_by === "doctor" ? <span className="font-bold text-red-800">Cancelled by doctor</span> : a.cancelled_by === "patient" ? <span className="font-bold text-red-800">Cancelled by patient</span> : null}
                          {a.cancellation_reason ? <span className="text-red-600">· {a.cancellation_reason}</span> : null}
                        </span>
                      )}
                      {a.is_follow_up && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs font-semibold">Follow-up</span>
                      )}
                    </div>
                    {isReschedulePending && (
                      <div className="w-full mt-3 rounded-xl bg-amber-100 border border-amber-200 px-4 py-3 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <WarningCircle size={16} weight="fill" className="text-amber-600 shrink-0" />
                          <span className="text-sm text-amber-800">This appointment needs to be rescheduled.</span>
                        </div>
                        <Link to="/doctors" className="text-xs font-semibold text-primary hover:underline whitespace-nowrap">Rebook →</Link>
                      </div>
                    )}
                    {isActive && !isReschedulePending && (
                      <div className="flex gap-2 ml-auto">
                        <Button variant="outline" onClick={() => cancel(a.id)} className="rounded-full h-9 px-4 text-xs text-destructive border-destructive/30 hover:bg-destructive/10">
                          Cancel
                        </Button>
                        <Link to="/my-queue">
                          <Button variant="outline" className="rounded-full h-9 px-4 text-xs border-primary text-primary">
                            <Clock size={14} weight="duotone" /> Live
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <Dialog open={cancelDialog.open} onOpenChange={(open) => { if (!open) setCancelDialog({ open: false, id: null }); }}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Cancel appointment</DialogTitle>
            <DialogDescription>
              Tell us why you're cancelling (optional):
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Enter reason..."
            className="w-full rounded-xl border border-input bg-background p-3 text-sm min-h-[100px] resize-none outline-none focus:ring-2 focus:ring-primary"
          />
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setCancelDialog({ open: false, id: null })}
              className="flex-1 rounded-full h-12"
            >
              Keep it
            </Button>
            <Button
              onClick={confirmCancel}
              className="flex-1 rounded-full h-12 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={recordsOpen} onOpenChange={setRecordsOpen}>
        <DialogContent className="rounded-3xl max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">My medical records</DialogTitle>
            <DialogDescription>Upload prescriptions, reports, and documents that you can share with doctors.</DialogDescription>
          </DialogHeader>
          <form onSubmit={uploadRecord} className="rounded-2xl border border-border bg-card p-4 grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Title</Label>
              <Input value={uploadForm.title} onChange={(e) => setUploadForm((s) => ({ ...s, title: e.target.value }))} placeholder="Blood report, prescription…" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label>Type</Label>
              <select
                value={uploadForm.record_type}
                onChange={(e) => setUploadForm((s) => ({ ...s, record_type: e.target.value }))}
                className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="report">Report</option>
                <option value="prescription">Prescription</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label>File</Label>
              <Input
                type="file"
                accept="application/pdf,image/png,image/jpeg,image/webp,.doc,.docx"
                onChange={(e) => setUploadForm((s) => ({ ...s, file: e.target.files?.[0] || null }))}
                className="mt-1.5 h-11"
              />
            </div>
            <div className="sm:col-span-2">
              <Button disabled={uploading} className="rounded-full bg-primary text-primary-foreground hover:bg-[#1a3025]">
                <UploadSimple size={18} weight="duotone" /> <span className="ml-1.5">{uploading ? "Uploading…" : "Upload"}</span>
              </Button>
            </div>
          </form>
          {records.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-8 text-center text-muted-foreground">No records uploaded yet.</div>
          ) : (
            <div className="rounded-2xl border border-border bg-card divide-row overflow-hidden">
              {records.map((record) => (
                <div key={record.id} className="px-5 py-4 flex items-center gap-3 hover:bg-muted/40 group">
                  <button
                    type="button"
                    onClick={() => setPreviewRecord(record)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    <FileText size={20} weight="duotone" className="text-primary" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-foreground break-words">{record.title}</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-[0.16em]">{record.record_type} · {record.created_at ? new Date(record.created_at).toLocaleDateString() : ""}</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteRecord(record.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    title="Delete record"
                  >
                    <Trash size={18} weight="duotone" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <MedicalRecordPreviewModal
            record={previewRecord}
            open={!!previewRecord}
            onClose={() => setPreviewRecord(null)}
            canDelete
            onDelete={(id) => { setPreviewRecord(null); deleteRecord(id); }}
          />
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl border border-dashed border-border p-12 text-center" data-testid="empty-appointments">
      <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center text-primary mb-4">
        <Note size={26} weight="duotone" />
      </div>
      <h2 className="font-display font-bold text-2xl text-foreground tracking-tight">No active appointments</h2>
      <p className="mt-2 text-muted-foreground">Find a doctor and book your first walk-in token.</p>
      <Link to="/doctors" className="inline-block mt-6">
        <Button className="rounded-full h-12 px-6 bg-primary text-primary-foreground hover:bg-[#1a3025]" data-testid="empty-find-btn">
          <MagnifyingGlass size={18} weight="duotone" /> <span className="ml-1.5">Find a doctor</span>
        </Button>
      </Link>
    </div>
  );
}

function AppointmentCard({ item, onCancel, onReschedule }) {
  const a = item.appointment;
  const d = item.doctor;
  const meta = STATUS_META[a.status];
  const Icon = meta.icon;
  const isWaiting = a.status === "waiting";
  const isActive = isWaiting || a.status === "in_progress";
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [newDate, setNewDate] = useState(a.queue_date);
  const [rescheduleAvailability, setRescheduleAvailability] = useState(null);
  const [rescheduleSlot, setRescheduleSlot] = useState(null);

  useEffect(() => {
    if (!rescheduleOpen || !newDate || !d?.id) return;
    const params = { queue_date: newDate, location_id: a.location_id || undefined };
    api.get(`/doctors/${d.id}/availability`, { params }).then(({ data }) => {
      setRescheduleAvailability(data);
      setRescheduleSlot(null);
    }).catch(() => setRescheduleAvailability(null));
  }, [rescheduleOpen, newDate, d?.id, a.location_id]);

  return (
    <article className="rounded-3xl border border-border bg-card p-5 sm:p-7 lg:p-8 grid lg:grid-cols-5 gap-5 sm:gap-6" data-testid={`appt-card-${a.id}`}>
      <div className="lg:col-span-3">
        <div className="flex items-start gap-3 sm:gap-4">
          {d?.photo_url && (
            <img src={d.photo_url} alt={d.name} className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl object-cover flex-shrink-0" />
          )}
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.18em] text-accent">{d?.specialty}</div>
            <div className="font-display font-bold text-xl sm:text-2xl text-foreground tracking-tight leading-tight break-words" data-testid={`appt-doctor-${a.id}`}>
              {d?.name || a.doctor_name}
            </div>
            <div className="text-sm text-muted-foreground break-words">{d?.clinic_name}</div>
          </div>
        </div>

        <div className="mt-5 sm:mt-6">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Your token</div>
          <div className="font-token text-6xl sm:text-7xl text-primary mt-1 leading-none" data-testid={`my-token-${a.id}`}>
            #{String(a.token_number).padStart(2, "0")}
          </div>
          <div className="text-sm text-muted-foreground mt-2">{a.queue_date}</div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: meta.color + "1A", color: meta.color }}>
              <Icon size={16} weight="duotone" />
              <span className="text-sm font-semibold">{meta.label}</span>
            </div>
            {a.is_follow_up && (
              <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold">
                Follow-up
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 space-y-1">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Reason</div>
          <div className="text-foreground">{a.reason}</div>
        </div>

        {isActive && (
          <div className="mt-5 flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setRescheduleOpen(true)}
              className="rounded-full"
              data-testid={`reschedule-${a.id}`}
            >
              <CalendarBlank size={16} weight="duotone" /> <span className="ml-1.5">Reschedule</span>
            </Button>
            <Link to="/medical-records">
              <Button
                variant="outline"
                className="rounded-full"
                data-testid={`records-access-${a.id}`}
              >
                <ShareNetwork size={16} weight="duotone" /> <span className="ml-1.5">Share records</span>
              </Button>
            </Link>
            <Button
              variant="ghost"
              onClick={() => onCancel(a.id)}
              className="text-destructive hover:text-destructive hover:bg-destructive/10 rounded-full"
              data-testid={`cancel-${a.id}`}
            >
              Cancel my appointment
            </Button>
          </div>
        )}
      </div>

      <div className="lg:col-span-2 grid gap-3 content-start">
        <div className="rounded-2xl bg-primary text-primary-foreground p-5">
          <div className="text-xs uppercase tracking-[0.2em] opacity-80">Currently serving</div>
          <div className="font-token text-4xl mt-1.5" data-testid={`serving-${a.id}`}>
            {item.currently_serving ? `#${String(item.currently_serving.token_number).padStart(2, "0")}` : "—"}
          </div>
          <div className="text-xs mt-1 opacity-80">
            {item.currently_serving ? item.currently_serving.patient_name : "No one in the chair yet"}
          </div>
        </div>

        <div className="rounded-2xl border border-border p-5 bg-card">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">People ahead</div>
          <div className="font-token text-4xl text-foreground mt-1.5" data-testid={`ahead-${a.id}`}>
            {a.status === "in_progress" ? 0 : item.ahead_count}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {a.status === "in_progress" ? "You're up — head in!" : item.ahead_count === 0 ? "You're next in line" : `${item.ahead_count} before you`}
          </div>
        </div>

        <div className="rounded-2xl bg-accent/15 border border-accent/30 p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-accent">Estimated wait</div>
          <div className="font-token text-4xl text-foreground mt-1.5 leading-none" data-testid={`eta-${a.id}`}>
            {a.status === "in_progress" ? "0" : item.estimated_wait_minutes === 0 ? "<5" : `~${item.estimated_wait_minutes}`}
            <span className="font-display font-bold text-xl ml-1.5 text-muted-foreground">min</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">~{item.avg_consult_minutes} min per consult today</div>
        </div>
      </div>
      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Reschedule appointment</DialogTitle>
            <DialogDescription>Choose a new appointment date with {d?.name || a.doctor_name}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New date</Label>
              <DateTimeField
                mode="date"
                minDate={new Date().toISOString().slice(0, 10)}
                value={newDate}
                onChange={setNewDate}
                placeholder="Choose new date"
                className="mt-2"
              />
            </div>
            {false && rescheduleAvailability && d?.booking_mode === "slot_based" && (
              <div>
                <Label className="text-xs font-semibold text-muted-foreground mb-2 block">Select a time slot</Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                  {rescheduleAvailability.sessions?.map((session) =>
                    session.slots?.filter(s => s.available).map((slot) => (
                      <button
                        key={`${session.id}-${slot.start_time}`}
                        type="button"
                        onClick={() => setRescheduleSlot(slot)}
                        className={`text-xs py-2 rounded-lg border font-semibold transition-all ${
                          rescheduleSlot?.start_time === slot.start_time
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background text-foreground border-border hover:border-primary/40"
                        }`}
                      >
                        {slot.short_label || slot.start_time?.slice(0, 5)}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
            {rescheduleAvailability && !rescheduleAvailability.available && (
              <p className="text-xs text-destructive font-semibold">{rescheduleAvailability.message || "No slots available on this date"}</p>
            )}
          </div>
          <Button
            disabled={!rescheduleAvailability?.available}
            onClick={async () => {
              await onReschedule(a.id, newDate, rescheduleSlot?.start_time, rescheduleSlot?.end_time);
              setRescheduleOpen(false);
            }}
            className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-[#1a3025]"
          >
            Confirm reschedule
          </Button>
        </DialogContent>
      </Dialog>
    </article>
  );
}
